import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import InfraNav from './components/InfraNav';
import InfraFooter from './components/InfraFooter';
import InfraCTA from './components/InfraCTA';
import './infrastructure.css';

export default function InfrastructureCollect() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="infra-page" data-testid="infrastructure-collect">
      <InfraNav transparent={false} />

      {/* ══════ HERO ══════ */}
      <div className="infra-product-hero">
        <div className="infra-product-hero-text">
          <div className="infra-product-eyebrow" style={{ color: 'var(--pebbles-purple)' }}>COLLECT</div>
          <h1 className="infra-product-headline">Stop chasing payments. Deduct at source.</h1>
          <p className="infra-product-desc">
            Any service that needs recurring payments from employees plugs into Pebbles Collect. Loans, insurance, subscriptions, cooperatives — 99% collection rate because we sit at payroll.
          </p>
          <div className="infra-product-buttons">
            <Link to="/login" className="infra-btn-primary" data-testid="collect-get-started">Get started</Link>
            <a href="mailto:hello@pebbles.financial" className="infra-btn-secondary">Contact sales</a>
          </div>
        </div>
        <div className="infra-product-hero-mockup">
          <div className="infra-feature-card">
            <div className="infra-feature-card-inner">
              <div className="infra-mockup-label" style={{ marginBottom: 10 }}>Collection partners</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {[
                  { name: 'GDL+', sub: 'Subscription · 342 active', amt: '₦1.2M', rate: '99.1%' },
                  { name: 'StaffCoop', sub: 'Loan repayment · 128 active', amt: '₦890K', rate: '100%' },
                  { name: 'AXA Mansard', sub: 'Insurance · 215 active', amt: '₦645K', rate: '98.6%' },
                ].map(p => (
                  <div key={p.name} className="infra-data-row" style={{ border: '1px solid var(--pebbles-gray-100)', borderRadius: 8 }}>
                    <div><div style={{ fontSize: 12, fontWeight: 500, color: 'var(--pebbles-dark)' }}>{p.name}</div><div style={{ fontSize: 10, color: 'var(--pebbles-gray-400)' }}>{p.sub}</div></div>
                    <div style={{ textAlign: 'right' }}><div style={{ fontSize: 12, fontWeight: 500, color: 'var(--pebbles-dark)' }}>{p.amt}</div><div style={{ fontSize: 10, color: 'var(--pebbles-green)' }}>{p.rate}</div></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ══════ FEATURE 1: Collect Links ══════ */}
      <div className="infra-feature">
        <div className="infra-feature-inner">
          <div className="infra-feature-text">
            <span className="infra-feature-label" style={{ background: 'rgba(127,119,221,0.1)', color: 'var(--pebbles-purple)' }}>Collect Links</span>
            <h3 className="infra-feature-title">Create a product. Share a link. Get paid.</h3>
            <p className="infra-feature-desc">
              No technical knowledge required. Create a subscription or product in the Pebbles dashboard, get a payment link, share it anywhere. For coaches, creators, cooperatives, landlords, churches.
            </p>
            <ul className="infra-feature-bullets infra-check-purple">
              <li>Auto-generated checkout page</li>
              <li>Shareable on WhatsApp, social, email</li>
              <li>One-time and recurring payments</li>
              <li>Zero code required</li>
            </ul>
          </div>
          <div className="infra-feature-mockup">
            {/* Phone-shaped checkout mockup */}
            <div style={{ maxWidth: 260, margin: '0 auto', background: '#fff', borderRadius: 24, border: '1px solid var(--pebbles-gray-200)', padding: '32px 20px', boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}>
              <div style={{ width: 40, height: 4, borderRadius: 2, background: 'var(--pebbles-gray-200)', margin: '0 auto 20px' }} />
              <div style={{ textAlign: 'center', marginBottom: 20 }}>
                <div style={{ width: 48, height: 48, borderRadius: 12, background: 'rgba(127,119,221,0.1)', margin: '0 auto 12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontSize: 20, color: 'var(--pebbles-purple)' }}>S</span>
                </div>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--pebbles-dark)' }}>StaffCoop Monthly</div>
                <div style={{ fontSize: 12, color: 'var(--pebbles-gray-400)', marginTop: 4 }}>Cooperative savings</div>
              </div>
              <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--pebbles-dark)', textAlign: 'center', marginBottom: 20 }}>₦25,000<span style={{ fontSize: 12, fontWeight: 400, color: 'var(--pebbles-gray-400)' }}>/mo</span></div>
              <div style={{ background: 'var(--pebbles-purple)', color: '#fff', textAlign: 'center', padding: '12px 0', borderRadius: 12, fontSize: 14, fontWeight: 500 }}>Pay with Pebbles</div>
              <div style={{ fontSize: 10, color: 'var(--pebbles-gray-400)', textAlign: 'center', marginTop: 12 }}>Secured by Pebbles</div>
            </div>
          </div>
        </div>
      </div>

      {/* ══════ FEATURE 2: Collect SDK ══════ */}
      <div className="infra-feature infra-feature--reverse infra-feature--gray">
        <div className="infra-feature-inner">
          <div className="infra-feature-text">
            <span className="infra-feature-label" style={{ background: 'rgba(127,119,221,0.1)', color: 'var(--pebbles-purple)' }}>Collect SDK</span>
            <h3 className="infra-feature-title">Full integration for apps and platforms</h3>
            <p className="infra-feature-desc">
              REST API for creating plans, managing subscriptions, and checking entitlements. Drop the Pebbles SDK into your app and let your users pay with their wallet.
            </p>
            <ul className="infra-feature-bullets infra-check-purple">
              <li>Create and manage subscription plans</li>
              <li>Check entitlements in real-time</li>
              <li>Webhook notifications for all events</li>
              <li>Client libraries for Python, JavaScript, PHP</li>
            </ul>
          </div>
          <div className="infra-feature-mockup">
            <div className="infra-feature-card" style={{ padding: 0 }}>
              <div className="infra-code-mockup" style={{ margin: 0, borderRadius: 14 }}>
                <pre>
<span className="infra-syn-kw">const</span> <span className="infra-syn-def">pebbles</span> = <span className="infra-syn-fn">require</span>(<span className="infra-syn-str">'@pebbles/sdk'</span>);{'\n'}{'\n'}<span className="infra-syn-kw">const</span> <span className="infra-syn-def">plan</span> = <span className="infra-syn-kw">await</span> pebbles.plans<span className="infra-syn-fn">.create</span>({'{'}{'\n'}{'  '}name: <span className="infra-syn-str">'Premium Monthly'</span>,{'\n'}{'  '}amount: <span className="infra-syn-num">25000</span>,{'\n'}{'  '}interval: <span className="infra-syn-str">'monthly'</span>,{'\n'}{'  '}currency: <span className="infra-syn-str">'NGN'</span>{'\n'}{'}'});{'\n'}{'\n'}<span className="infra-syn-kw">const</span> <span className="infra-syn-def">sub</span> = <span className="infra-syn-kw">await</span> pebbles.subscriptions{'\n'}{'  '}<span className="infra-syn-fn">.create</span>({'{'}{'\n'}{'    '}plan: plan<span className="infra-syn-fn">.id</span>,{'\n'}{'    '}employee: <span className="infra-syn-str">'emp_9x2kf4'</span>,{'\n'}{'    '}start_date: <span className="infra-syn-str">'2026-04-01'</span>{'\n'}{'  '}{'}'});{'\n'}{'\n'}console<span className="infra-syn-fn">.log</span>(sub.status); <span style={{color:'#6c7086'}}>// "active"</span>
                </pre>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ══════ FEATURE 3: Analytics ══════ */}
      <div className="infra-feature">
        <div className="infra-feature-inner">
          <div className="infra-feature-text">
            <span className="infra-feature-label" style={{ background: 'rgba(127,119,221,0.1)', color: 'var(--pebbles-purple)' }}>Analytics</span>
            <h3 className="infra-feature-title">Revenue insights, instantly</h3>
            <p className="infra-feature-desc">
              Track MRR, collection success rates, churn, and revenue by cohort. Understand exactly how your recurring revenue performs.
            </p>
            <ul className="infra-feature-bullets infra-check-purple">
              <li>Near-realtime revenue dashboard</li>
              <li>Collection success rate tracking</li>
              <li>Churn rate and reasons</li>
              <li>Revenue by product and cohort</li>
            </ul>
          </div>
          <div className="infra-feature-mockup">
            <div className="infra-feature-card">
              <div className="infra-feature-card-inner">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
                  <div style={{ background: '#fff', borderRadius: 8, padding: 12, border: '1px solid var(--pebbles-gray-100)' }}>
                    <div style={{ fontSize: 10, color: 'var(--pebbles-gray-400)', marginBottom: 4 }}>MRR</div>
                    <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--pebbles-dark)' }}>₦8.4M</div>
                    <div style={{ fontSize: 10, color: 'var(--pebbles-green)', marginTop: 2 }}>+12% vs last month</div>
                  </div>
                  <div style={{ background: '#fff', borderRadius: 8, padding: 12, border: '1px solid var(--pebbles-gray-100)' }}>
                    <div style={{ fontSize: 10, color: 'var(--pebbles-gray-400)', marginBottom: 4 }}>Success Rate</div>
                    <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--pebbles-dark)' }}>99.1%</div>
                    <div style={{ fontSize: 10, color: 'var(--pebbles-green)', marginTop: 2 }}>685 of 691 collected</div>
                  </div>
                </div>
                <div className="infra-mini-chart">
                  {[40, 55, 35, 60, 50, 75, 65, 80, 70, 85, 78, 90].map((h, i) => (
                    <div key={i} className="infra-mini-chart-bar" style={{ height: `${h}%`, background: i === 11 ? 'var(--pebbles-purple)' : 'rgba(127,119,221,0.3)' }} />
                  ))}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
                  <span style={{ fontSize: 9, color: 'var(--pebbles-gray-400)' }}>Jan</span>
                  <span style={{ fontSize: 9, color: 'var(--pebbles-gray-400)' }}>Dec</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <InfraCTA />
      <InfraFooter />
    </div>
  );
}
